// src/components/PatientNotesTimeline.jsx
//
// Append-only coordinator notes for a patient.
// Writes to patient_notes/{patientId}/notes/{noteId} and renders the notes
// as a read-only timeline.
//
// Notes themselves remain immutable. To correct or annotate a saved note,
// staff can add append-only *replies* threaded under it, stored at
// patient_notes/{patientId}/notes/{noteId}/replies/{replyId}. Replies are
// audit-logged and never mutate the original note (see NoteReplies below).

import { useEffect, useState } from "react";
import NotesViewerModal from "./patient/NotesViewerModal";
import { db, auth } from "../firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  getPatientNotesEntriesPath,
  getPatientNoteRepliesPath,
} from "../services/firestorePaths";
import {
  AlertTriangle,
  Clock,
  CornerDownRight,
  Loader2,
  Maximize2,
  MessageSquarePlus,
  Reply,
  Save,
  StickyNote,
  User,
} from "lucide-react";
import { logPatientAuditEvent } from "../services/patientAuditService";

const formatTimestamp = (ts) => {
  if (!ts) return "Pending timestamp";
  try {
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "Pending timestamp";
  }
};

// -----------------------------------------------------------------------
// NoteReplies — append-only replies/annotations threaded under a single
// saved note. This is strictly additive: it never reads or mutates the
// parent note. Each reply is its own immutable doc + audit event.
// -----------------------------------------------------------------------
const NoteReplies = ({
  patientId,
  note,
  patientName,
  mrnLast4,
  source,
  actorRole,
  canReply,
}) => {
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!patientId || !note?.id) {
      setReplies([]);
      return undefined;
    }

    const q = query(
      collection(db, getPatientNoteRepliesPath(patientId, note.id)),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => setReplies(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Patient note replies subscribe error:", err)
    );

    return () => unsubscribe();
  }, [patientId, note?.id]);

  const handleSaveReply = async () => {
    const body = draft.trim();
    const user = auth.currentUser;

    if (!body || !patientId || !note?.id || !user || saving) return;

    try {
      setSaving(true);
      setError(null);

      const repliesRef = collection(
        db,
        getPatientNoteRepliesPath(patientId, note.id)
      );
      const replyRef = await addDoc(repliesRef, {
        body,
        patientId,
        noteId: note.id,
        patientName,
        mrnLast4,
        source,
        createdByEmail: user.email || "unknown",
        createdByUid: user.uid || null,
        createdByRole: actorRole || "",
        createdAt: serverTimestamp(),
        schemaVersion: 1,
      });

      setDraft("");
      setOpen(false);

      await logPatientAuditEvent({
        patientId,
        title: "Coordinator Note Reply Added",
        action: "COORDINATOR_NOTE_REPLY_ADDED",
        type: "WRITE",
        severity: "info",
        patientName,
        mrnLast4,
        source,
        actorEmail: user.email || "unknown",
        actorUid: user.uid || null,
        metadata: {
          page: "PatientDetailPage",
          recordPath: `${getPatientNoteRepliesPath(patientId, note.id)}/${
            replyRef.id
          }`,
          noteId: note.id,
          replyId: replyRef.id,
          replyPreview: body.slice(0, 80),
        },
      });
    } catch (err) {
      console.error("Save coordinator note reply failed:", err);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {replies.length > 0 && (
        <ul className="space-y-2 border-l border-white/10 pl-3 ml-1">
          {replies.map((reply) => (
            <li key={reply.id}>
              <p className="flex items-start gap-1.5 text-[14px] text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                <CornerDownRight className="w-3 h-3 mt-0.5 shrink-0 text-purple-300/70" />
                <span>{reply.body}</span>
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2 pl-4 text-[10px] text-slate-400">
                <span className="inline-flex items-center gap-1 min-w-0">
                  <User className="w-2.5 h-2.5 shrink-0" />
                  <span className="truncate max-w-[180px]">
                    {reply.createdByEmail || "unknown"}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />
                  {formatTimestamp(reply.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canReply && (
        <div className="pl-1">
          {open ? (
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-2">
              <textarea
                className="w-full px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition resize-y min-h-[64px]"
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Add a reply or correction to this note. The original note stays unchanged."
                disabled={saving}
                autoFocus
              />
              <div className="mt-1.5 flex items-center justify-end gap-2">
                {error && (
                  <span className="mr-auto inline-flex items-center gap-1 text-[11px] text-rose-300">
                    <AlertTriangle className="w-3 h-3" />
                    Reply could not be saved.
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setDraft("");
                    setError(null);
                  }}
                  disabled={saving}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-slate-300 hover:text-white hover:bg-white/[0.06] transition disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveReply}
                  disabled={saving || !draft.trim()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-[11px] font-semibold text-white hover:shadow-[0_6px_18px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  {saving ? "Saving..." : "Save Reply"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-300 hover:text-purple-200 transition"
            >
              <Reply className="w-3 h-3" />
              Reply
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const PatientNotesTimeline = ({
  patientId,
  patientName = "",
  mrnLast4 = "",
  source = "",
  actorRole = "",
  canAddNote = false,
  noteDraft,
  onNoteDraftChange,
}) => {
  const [notes, setNotes] = useState([]);
  const [internalNote, setInternalNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Read-only pop-up of every note (2026-10-26). Display only.
  const [viewerOpen, setViewerOpen] = useState(false);

  const isControlled = noteDraft !== undefined && onNoteDraftChange !== undefined;
  const newNote = isControlled ? noteDraft : internalNote;
  const setNewNote = isControlled ? onNoteDraftChange : setInternalNote;

  useEffect(() => {
    if (!patientId) {
      setNotes([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, getPatientNotesEntriesPath(patientId)),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Patient notes subscribe error:", err);
        setError(err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [patientId]);

  const handleSaveNote = async () => {
    const body = newNote.trim();
    const user = auth.currentUser;

    if (!body || !patientId || !user || saving) return;

    try {
      setSaving(true);
      setError(null);

      const notesRef = collection(db, getPatientNotesEntriesPath(patientId));
      const noteRef = await addDoc(notesRef, {
        body,
        patientId,
        patientName,
        mrnLast4,
        source,
        createdByEmail: user.email || "unknown",
        createdByUid: user.uid || null,
        createdByRole: actorRole || "",
        createdAt: serverTimestamp(),
        schemaVersion: 1,
      });

      setNewNote("");

      await logPatientAuditEvent({
        patientId,
        title: "Coordinator Note Added",
        action: "COORDINATOR_NOTE_ADDED",
        type: "WRITE",
        severity: "info",
        patientName,
        mrnLast4,
        source,
        actorEmail: user.email || "unknown",
        actorUid: user.uid || null,
        metadata: {
          page: "PatientDetailPage",
          recordPath: `${getPatientNotesEntriesPath(patientId)}/${noteRef.id}`,
          noteId: noteRef.id,
          notePreview: body.slice(0, 80),
        },
      });
    } catch (err) {
      console.error("Save coordinator note failed:", err);
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {canAddNote ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
          <label className="block">
            <span className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
              <span>Add Note</span>
              <span className="normal-case tracking-normal font-semibold text-rose-300">
                * Save notes before exiting
              </span>
            </span>
            <textarea
              className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition resize-y min-h-[130px] leading-6"
              rows={5}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a coordinator note for this patient. Make sure to save notes before exiting."
              disabled={saving}
            />
          </label>
          <div className="mt-2 flex items-center justify-end gap-2">
            {error && (
              <span className="mr-auto inline-flex items-center gap-1 text-[11px] text-rose-300">
                <AlertTriangle className="w-3 h-3" />
                Note could not be saved.
              </span>
            )}
            <button
              type="button"
              onClick={handleSaveNote}
              disabled={saving || !newNote.trim()}
              className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {saving ? "Saving..." : "Save Note"}
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3 text-[11px] text-slate-400">
          Coordinator notes are read-only for this role.
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-white/[0.025] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2">
          <StickyNote className="w-3.5 h-3.5 text-purple-300" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
            Note Timeline
          </span>
          <span className="ml-auto text-[10px] font-mono text-slate-400">
            {notes.length}
          </span>
          {notes.length > 0 && (
            <button
              type="button"
              onClick={() => setViewerOpen(true)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition notes-open-btn"
            >
              <Maximize2 className="w-3 h-3" />
              Open larger
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-4 text-center text-[11px] text-slate-400">
            Loading notes...
          </div>
        ) : error && notes.length === 0 ? (
          <div className="p-4 text-center text-[11px] text-rose-300">
            Failed to load notes.
          </div>
        ) : notes.length === 0 ? (
          <div className="p-5 text-center">
            <div className="mx-auto w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center mb-2">
              <MessageSquarePlus className="w-4 h-4 text-purple-300" />
            </div>
            <p className="text-xs font-semibold text-white">
              No coordinator notes yet
            </p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Saved notes will appear here as a read-only timeline.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {notes.map((note) => (
              <li key={note.id} className="p-4">
                <p className="text-[15px] text-slate-100 whitespace-pre-wrap break-words leading-7">
                  {note.body}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <User className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate max-w-[180px]">
                      {note.createdByEmail || "unknown"}
                    </span>
                  </span>
                  {note.createdByUid && (
                    <span className="font-mono text-slate-500 truncate max-w-[180px]">
                      uid: {note.createdByUid}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {formatTimestamp(note.createdAt)}
                  </span>
                </div>

                <NoteReplies
                  patientId={patientId}
                  note={note}
                  patientName={patientName}
                  mrnLast4={mrnLast4}
                  source={source}
                  actorRole={actorRole}
                  canReply={canAddNote}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <NotesViewerModal
        open={viewerOpen}
        title="Coordinator Notes"
        subtitle={[patientName, `${notes.length} note${notes.length === 1 ? "" : "s"}, newest first · read only`].filter(Boolean).join(" · ")}
        onClose={() => setViewerOpen(false)}
      >
        <ul className="space-y-4">
          {notes.map((note) => (
            <li key={note.id} className="rounded-xl border p-4 notes-viewer-item">
              <p className="whitespace-pre-wrap break-words text-[15px] leading-7 notes-viewer-text">{note.body}</p>
              <p className="mt-2 text-xs notes-viewer-muted">
                {note.createdByEmail || "unknown"} · {formatTimestamp(note.createdAt)}
              </p>
              <NoteReplies
                patientId={patientId}
                note={note}
                patientName={patientName}
                mrnLast4={mrnLast4}
                source={source}
                actorRole={actorRole}
                canReply={false}
              />
            </li>
          ))}
        </ul>
      </NotesViewerModal>
    </div>
  );
};

export default PatientNotesTimeline;
